import React, { useState, useContext, useEffect } from 'react';
import { XmplContext, useAdors, useEvents } from 'xmpl-react';
import ProductCheckboxes from '../components/ProductCheckboxes';

/**
 * PreferencesPage — personalized preference-management form.
 *
 * Requires a `rid` query-parameter (or a cached `xmpRecipientID` in
 * localStorage).  On mount it fetches the recipient's current ADOR
 * values from Circle and pre-fills the form.  Submitting writes the
 * updated values back via `updateAdors` and logs a Circle event.
 *
 * If no `rid` is available the page renders an "Access Denied" message
 * with a link back to the registration form.
 */
export default function PreferencesPage() {
  const { xmp } = useContext(XmplContext);
  const { getAdorValues, updateAdors } = useAdors();
  const { events } = useEvents();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [subscribeAll, setSubscribeAll] = useState(false);
  const [subscriptions, setSubscriptions] = useState({
    Sub_uCreatePrint: false,
    Sub_uProduce: false,
    Sub_uStore: false,
    Sub_Circle: false,
    Sub_uPlan: false,
    Sub_XMPieVideoService: false,
    Sub_XMPieEmailService: false,
    Sub_uImage: false,
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const rid = new URLSearchParams(window.location.search).get('rid')
    || localStorage.getItem('xmpRecipientID');

  useEffect(() => {
    if (rid) {
      getAdorValues({
        rid,
        isLogin: true,
        adors: [
          'FirstName', 'LastName', 'Email', 'Company', 'JobTitle', 'Phone', 'Country',
          'SubscribeAll',
          'Sub_uCreatePrint', 'Sub_uProduce', 'Sub_uStore', 'Sub_Circle',
          'Sub_uPlan', 'Sub_XMPieVideoService', 'Sub_XMPieEmailService', 'Sub_uImage',
        ],
        async: false,
        isCached: false,
        noCache: true,
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (xmp && xmp.r && xmp.r['FirstName'] !== undefined) {
      setFirstName(xmp.r['FirstName'] || '');
      setLastName(xmp.r['LastName'] || '');
      setEmail(xmp.r['Email'] || '');
      setCompany(xmp.r['Company'] || '');
      setJobTitle(xmp.r['JobTitle'] || '');
      setPhone(xmp.r['Phone'] || '');
      setCountry(xmp.r['Country'] || '');

      const toBool = (val) => String(val).toUpperCase() === 'TRUE';

      setSubscribeAll(toBool(xmp.r['SubscribeAll']));
      setSubscriptions({
        Sub_uCreatePrint: toBool(xmp.r['Sub_uCreatePrint']),
        Sub_uProduce: toBool(xmp.r['Sub_uProduce']),
        Sub_uStore: toBool(xmp.r['Sub_uStore']),
        Sub_Circle: toBool(xmp.r['Sub_Circle']),
        Sub_uPlan: toBool(xmp.r['Sub_uPlan']),
        Sub_XMPieVideoService: toBool(xmp.r['Sub_XMPieVideoService']),
        Sub_XMPieEmailService: toBool(xmp.r['Sub_XMPieEmailService']),
        Sub_uImage: toBool(xmp.r['Sub_uImage']),
      });
      setLoaded(true);
    }
  }, [xmp]);

  const trackEvent = (actionName) => {
    const recipientID = rid || localStorage.getItem('xmpRecipientID');
    if (!recipientID) return;
    events({
      sync: false,
      recipientID,
      PageName: 'Preferences',
      ActionName: actionName,
      ActionParams: '',
      type: 'mousedown',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSubmitting(true);

    const today = new Date().toISOString().split('T')[0];

    try {
      const res = await updateAdors({
        FirstName: firstName,
        LastName: lastName,
        Email: email,
        Company: company,
        JobTitle: jobTitle,
        Phone: phone,
        Country: country,
        SubscribeAll: subscribeAll ? 'TRUE' : 'FALSE',
        Sub_uCreatePrint: subscriptions.Sub_uCreatePrint ? 'TRUE' : 'FALSE',
        Sub_uProduce: subscriptions.Sub_uProduce ? 'TRUE' : 'FALSE',
        Sub_uStore: subscriptions.Sub_uStore ? 'TRUE' : 'FALSE',
        Sub_Circle: subscriptions.Sub_Circle ? 'TRUE' : 'FALSE',
        Sub_uPlan: subscriptions.Sub_uPlan ? 'TRUE' : 'FALSE',
        Sub_XMPieVideoService: subscriptions.Sub_XMPieVideoService ? 'TRUE' : 'FALSE',
        Sub_XMPieEmailService: subscriptions.Sub_XMPieEmailService ? 'TRUE' : 'FALSE',
        Sub_uImage: subscriptions.Sub_uImage ? 'TRUE' : 'FALSE',
        LastUpdated: today,
      });

      if (res) {
        setSuccess('Your preferences have been updated successfully.');
        trackEvent('Updated preferences');
      } else {
        setError('Update failed. Please try again.');
      }
    } catch (err) {
      console.error('Update error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!rid) {
    return (
      <div className="page-container">
        <div className="form-card">
          <h2>Access Denied</h2>
          <p>Please use the personalized link from your email to access your preferences.</p>
          <p>If you haven't registered yet, <a href="/">click here to register</a>.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-hero">
        <h1>Manage Your Preferences</h1>
        <p>
          {loaded && firstName
            ? `Welcome back, ${firstName}. Update your security notification preferences below.`
            : 'Loading your preferences...'}
        </p>
      </div>

      <div className="form-card">
        <h2>Update Your Details</h2>

        {error && <div className="form-error">{error}</div>}
        {success && <div className="form-success">{success}</div>}

        <form onSubmit={handleSubmit} noValidate>
          <div className="form-section">
            <h3>Personal Information</h3>
            <div className="form-grid">
              <div className="form-field">
                <label htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                />
              </div>
              <div className="form-field">
                <label htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                />
              </div>
              <div className="form-field form-field--full">
                <label htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div className="form-field">
                <label htmlFor="company">Company</label>
                <input
                  type="text"
                  id="company"
                  value={company}
                  onChange={(e) => setCompany(e.target.value)}
                />
              </div>
              <div className="form-field">
                <label htmlFor="jobTitle">Job Title</label>
                <input
                  type="text"
                  id="jobTitle"
                  value={jobTitle}
                  onChange={(e) => setJobTitle(e.target.value)}
                />
              </div>
              <div className="form-field">
                <label htmlFor="phone">Phone</label>
                <input
                  type="tel"
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                />
              </div>
              <div className="form-field">
                <label htmlFor="country">Country</label>
                <input
                  type="text"
                  id="country"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Product Subscriptions</h3>
            <p className="form-hint">Select the products for which you would like to receive security updates.</p>
            <ProductCheckboxes
              subscriptions={subscriptions}
              onChange={setSubscriptions}
              subscribeAll={subscribeAll}
              onSubscribeAllChange={setSubscribeAll}
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={submitting}>
              {submitting ? 'Saving...' : 'Save Preferences'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
